import { Networks } from "impermax-sdk";

export interface ChainDetails {
  networkId: number;
  networkName: Networks;
}

export type ChainDetailsMap = {
  [key in Networks]: ChainDetails;
};

export const chainDetailsMap: ChainDetailsMap = {
  [Networks.Ropsten]: {
    networkId: 3,
    networkName: Networks.Ropsten,
  },
  [Networks.Mainnet]: {
    networkId: 1,
    networkName: Networks.Mainnet,
  },
  [Networks.Polygon]: {
    networkId: 137,
    networkName: Networks.Mainnet,
  },
  [Networks.Arbitrum]: {
    networkId: 42161,
    networkName: Networks.Arbitrum,
  },
  [Networks.Avalanche]: {
    networkId: 43114,
    networkName: Networks.Avalanche,
  },
  [Networks.Moonriver]: {
    networkId: 1285,
    networkName: Networks.Moonriver,
  },
  [Networks.Aurora]: {
    networkId: 1313161554,
    networkName: Networks.Aurora,
  },
  [Networks.Cronos]: {
    networkId: 25,
    networkName: Networks.Cronos,
  },
  [Networks.Fantom]: {
    networkId: 250,
    networkName: Networks.Fantom,
  },
  [Networks.Harmony]: {
    networkId: 1666600000,
    networkName: Networks.Harmony,
  },
  [Networks.Moonbeam]: {
    networkId: 1284,
    networkName: Networks.Moonbeam,
  },
  [Networks.Sxnetwork]: {
    networkId: 416,
    networkName: Networks.Sxnetwork,
  },
  [Networks.Canto]: {
    networkId: 7700,
    networkName: Networks.Canto,
  },
  [Networks.ZksyncEra]: {
    networkId: 324,
    networkName: Networks.ZksyncEra,
  },
  [Networks.Base]: {
    networkId: 8453,
    networkName: Networks.Base,
  },
  [Networks.Mantle]: {
    networkId: 5000,
    networkName: Networks.Mantle,
  },
  [Networks.Scroll]: {
    networkId: 534352,
    networkName: Networks.Scroll,
  },
  [Networks.Optimism]: {
    networkId: 10,
    networkName: Networks.Optimism,
  },
  [Networks.Real]: {
    networkId: 111188,
    networkName: Networks.Real,
  },
  [Networks.Blast]: {
    networkId: 81457,
    networkName: Networks.Blast,
  },
  [Networks.Sonic]: {
    networkId: 146,
    networkName: Networks.Sonic,
  },
  [Networks.Linea]: {
    networkId: 59144,
    networkName: Networks.Linea,
  },
};

export interface SwitchChainData {
  chainId: string; // hexadecimal chainId
  chainName?: string;
  nativeCurrency?: {
    name: string;
    symbol: string;
    decimals: number;
  };
  rpcUrls?: string[];
  blockExplorerUrls?: string[];
}

export type SwitchChainDataMap = {
  [key in Networks]: SwitchChainData;
};

export const switchChainDataMap: SwitchChainDataMap = {
  [Networks.Ropsten]: {
    chainId: "0x" + (3).toString(16),
    blockExplorerUrls: ["https://etherscan.io/"],
  },
  [Networks.Mainnet]: {
    chainId: "0x" + (1).toString(16),
    blockExplorerUrls: ["https://etherscan.io/"],
  },
  [Networks.Polygon]: {
    chainId: "0x" + (137).toString(16),
    chainName: "Matic",
    nativeCurrency: {
      name: "MATIC",
      symbol: "MATIC",
      decimals: 18,
    },
    rpcUrls: ["https://polygon-rpc.com/"],
    blockExplorerUrls: ["https://polygonscan.com/"],
  },
  [Networks.Arbitrum]: {
    chainId: "0x" + (42161).toString(16),
    chainName: "Arbitrum",
    nativeCurrency: {
      name: "ETH",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: ["https://arb1.arbitrum.io/rpc/"],
    blockExplorerUrls: ["https://arbiscan.io/"],
  },
  [Networks.Avalanche]: {
    chainId: "0x" + (43114).toString(16),
    chainName: "Avalanche Mainnet C-Chain",
    nativeCurrency: {
      name: "AVAX",
      symbol: "AVAX",
      decimals: 18,
    },
    rpcUrls: ["https://api.avax.network/ext/bc/C/rpc"],
    blockExplorerUrls: ["https://cchain.explorer.avax.network/"],
  },
  [Networks.Moonriver]: {
    chainId: "0x" + (1285).toString(16),
    chainName: "Moonriver",
    nativeCurrency: {
      name: "MOVR",
      symbol: "MOVR",
      decimals: 18,
    },
    rpcUrls: ["https://rpc.moonriver.moonbeam.network"],
    blockExplorerUrls: ["https://moonriver.moonscan.io/"],
  },
  [Networks.Aurora]: {
    chainId: "0x" + (1313161554).toString(16),
    chainName: "Aurora",
    nativeCurrency: {
      name: "ETH",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: [
      "https://mainnet.aurora.dev/BCxrdgcnsd7uNy8fNz1wL1X2i8a29LZtNDwhZtgvtFm5",
    ],
    blockExplorerUrls: ["https://explorer.mainnet.aurora.dev/"],
  },
  [Networks.Cronos]: {
    chainId: "0x" + (25).toString(16),
    chainName: "Cronos",
    nativeCurrency: {
      name: "CRO",
      symbol: "CRO",
      decimals: 18,
    },
    rpcUrls: ["https://evm-cronos.crypto.org"],
    blockExplorerUrls: ["https://cronoscan.com/"],
  },
  [Networks.Fantom]: {
    chainId: "0x" + (250).toString(16),
    chainName: "Fantom",
    nativeCurrency: {
      name: "FTM",
      symbol: "FTM",
      decimals: 18,
    },
    rpcUrls: ["https://rpc.ftm.tools/"],
    blockExplorerUrls: ["https://ftmscan.com/"],
  },
  [Networks.Harmony]: {
    chainId: "0x" + (1666600000).toString(16),
    chainName: "Harmony",
    nativeCurrency: {
      name: "ONE",
      symbol: "ONE",
      decimals: 18,
    },
    rpcUrls: ["https://api.harmony.one"],
    blockExplorerUrls: ["https://explorer.harmony.one/"],
  },
  [Networks.Moonbeam]: {
    chainId: "0x" + (1284).toString(16),
    chainName: "Moonbeam",
    nativeCurrency: {
      name: "GLMR",
      symbol: "GLMR",
      decimals: 18,
    },
    rpcUrls: ["https://rpc.api.moonbeam.network"],
    blockExplorerUrls: ["https://moonbeam.moonscan.io/"],
  },
  [Networks.Sxnetwork]: {
    chainId: "0x" + (416).toString(16),
    chainName: "SX Network",
    nativeCurrency: {
      name: "SX",
      symbol: "SX",
      decimals: 18,
    },
    rpcUrls: ["https://rpc.sx.technology"],
    blockExplorerUrls: ["https://explorer.sx.technology/"],
  },
  [Networks.Canto]: {
    chainId: "0x" + (7700).toString(16),
    chainName: "Canto",
    nativeCurrency: {
      name: "CANTO",
      symbol: "CANTO",
      decimals: 18,
    },
    rpcUrls: ["https://mainnode.plexnode.org:8545"],
    blockExplorerUrls: ["https://tuber.build/"],
  },
  [Networks.ZksyncEra]: {
    chainId: "0x" + (324).toString(16),
    chainName: "zkSYNC ERA",
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: ["https://mainnet.era.zksync.io"],
    blockExplorerUrls: ["https://explorer.zksync.io/"],
  },
  [Networks.Base]: {
    chainId: "0x" + (8453).toString(16),
    chainName: "Base",
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: ["https://base.llamarpc.com"],
    blockExplorerUrls: ["https://basescan.org/"],
  },
  [Networks.Mantle]: {
    chainId: "0x" + (5000).toString(16),
    chainName: "Mantle",
    nativeCurrency: {
      name: "Mantle",
      symbol: "MNT",
      decimals: 18,
    },
    rpcUrls: [
      "https://rpc.ankr.com/mantle",
      "https://rpc.mantle.xyz",
      "https://mantle-mainnet.public.blastapi.io"
    ],
    blockExplorerUrls: ["https://explorer.mantle.xyz/"],
  },
  [Networks.Scroll]: {
    chainId: "0x" + (534352).toString(16),
    chainName: "Scroll",
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: [
      "https://rpc.ankr.com/scroll",
      "https://1rpc.io/scroll"
    ],
    blockExplorerUrls: ["https://scrollscan.com/"],
  },
  [Networks.Optimism]: {
    chainId: "0x" + (10).toString(16),
    chainName: "Optimism",
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: [
      "https://rpc.ankr.com/optimism",
      "https://1rpc.io/op"
    ],
    blockExplorerUrls: ["https://optimistic.etherscan.io/"],
  },
  [Networks.Real]: {
    chainId: "0x" + (111188).toString(16),
    chainName: "re.al",
    nativeCurrency: {
      name: "Ethereum",
      symbol: "reETH",
      decimals: 18,
    },
    rpcUrls: [
      "https://real.drpc.org",
      "https://tangible-real.gateway.tenderly.co"
    ],
    blockExplorerUrls: ["https://explorer.re.al/"],
  },
  [Networks.Blast]: {
    chainId: "0x" + (81457).toString(16),
    chainName: "Blast",
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: [
      "https://rpc.blast.io",
      "https://rpc.ankr.com/blast"
    ],
    blockExplorerUrls: ["https://blastscan.io/"],
  },
  [Networks.Sonic]: {
    chainId: "0x" + (146).toString(16),
    chainName: "Sonic",
    nativeCurrency: {
      name: "S",
      symbol: "S",
      decimals: 18,
    },
    rpcUrls: [
      "https://rpc.soniclabs.com"
    ],
    blockExplorerUrls: ["https://sonicscan.org/"],
  },
  [Networks.Linea]: {
    chainId: "0x" + (59144).toString(16),
    chainName: "Linea",
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
    rpcUrls: [
      "https://1rpc.io/linea",
      "https://linea.drpc.org"
    ],
    blockExplorerUrls: ["https://lineascan.build/"],
  },
};
